import { minutesAgo, minutesUntil } from "../../shared.js";
import type { AgentSettings, Autonomy, Economics } from "../../types.js";

/**
 * Pure derived-state + label + relative-time helpers for the marketing Agent
 * Status cockpit, extracted from `StatusPage.tsx`. `StatusPage.tsx` re-exports
 * them, so the existing test (which imports from `./StatusPage.js`) is
 * unchanged.
 */

// ── Derived agent state ──────────────────────────────────────────────

export type AgentState = "running" | "paused" | "sleeping";

export function deriveState(
  settings: AgentSettings | undefined,
  econ: Economics | undefined,
): AgentState {
  if (settings?.paused) return "paused";
  if (econ?.budget.exhausted) return "sleeping";
  return "running";
}

export function autonomyLabel(a: Autonomy): string {
  switch (a) {
    case "fully_auto":
      return "Full autopilot";
    case "manual":
      return "Manual";
    case "review_gated":
    default:
      return "Review-gated";
  }
}

// ── Relative-time formatting ─────────────────────────────────────────

export function agoFmt(iso: string | null): string {
  if (!iso) return "never";
  const mins = minutesAgo(iso);
  if (mins <= 0) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m ago` : `${h}h ago`;
}

export function untilFmt(iso: string | null): string {
  if (!iso) return "—";
  const mins = minutesUntil(iso);
  if (mins <= 0) return "due now";
  if (mins < 60) return `in ${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `in ${h}h ${m}m` : `in ${h}h`;
}
